import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toDateOnlyYmd } from "@mediatechgroup/calendar-date";
import { Input, Textarea, Button } from "../ui";
import { useTheme } from "../../context/ThemeContext";
import Tooltip from "../common/Tooltip";
import { v4 as uuidv4 } from "uuid";
import {
  getInputStyles,
  getPrimaryText,
  getSecondaryText,
} from "../../utils/themeUtils";
import ProductAutocomplete from "../common/dropdown/ProductAutocomplete";
import InvoiceBreakdownTable from "../staff/invoice/InvoiceBreakdownTable";
import { refundProcessPayment } from "../../api/paymentsAPI";
import { toast } from "react-toastify";
import { getPaymentErrorMessage } from "../../utils/paymentErrors";
import { useTenantFormat } from "../../hooks/useTenantFormat";
import { TOKENS } from "../../utils/designTokens";

interface Invoice {
  id: string;
  description: string;
  amount: number;
  metadata?: any;
  due_date?: string;
  type?: string; // 'invoice' | 'payment'
  [key: string]: any;
}

interface EditInvoiceModalProps {
  isOpen: boolean;
  isLoading: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onChange: (invoice: Invoice) => void;
  onSubmit: (updatedInvoice: any) => void;
  handelFetchMemberData?: () => void;
}

const EditBillingInvoiceModal: React.FC<EditInvoiceModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onChange,
  onSubmit,
  handelFetchMemberData,
}) => {
  const { isDarkMode } = useTheme();
  const { formatCurrency } = useTenantFormat();
  const [saving, setSaving] = useState(false);
  const [lineItems, setLineItems] = useState([
    { id: uuidv4(), quantity: 1, description: "", unitPrice: 0, discount: 0 },
  ]);
  useState<string | null>(
    null
  );
  useState<string | null>(
    null
  );
  // True once staff touch anything that sets the amount (a line item, the
  // Amount box). Until then a save keeps the stored amount, whatever the
  // line-item editor shows, and the server is told the amount did not change.
  const [amountTouched, setAmountTouched] = useState(false);
  const [refundMode, setRefundMode] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0);
  const token = localStorage.getItem("authToken");

  // 🔁 Load existing line items when modal opens
  //
  // An invoice with no saved line items (registration fees, camp and event
  // charges, prorated enrollments) is seeded with ONE line carrying its
  // current amount and description. It used to be seeded with a blank $0
  // line, and the save took the amount from the line items, so editing only
  // the note or the due date set the charge to $0 and erased what the family
  // owed (2026-10-10).
  const invoiceId = invoice?.id;
  useEffect(() => {
    if (!isOpen) return;
    setAmountTouched(false);
    const savedLineItems = invoice?.metadata?.lineItems;
    if (Array.isArray(savedLineItems) && savedLineItems.length > 0) {
      setLineItems(savedLineItems);
    } else {
      setLineItems([
        {
          id: uuidv4(),
          quantity: 1,
          description: invoice?.description || "",
          unitPrice: Number(invoice?.amount) || 0,
          discount: 0,
        },
      ]);
    }
    // Re-seed only when a different invoice is opened: `invoice` changes on
    // every keystroke in the description/note boxes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, isOpen]);

  if (!isOpen || !invoice) return null;

  const isPayment =
    invoice.type === "payment" || invoice.metadata?.type === "credit";

  // "Mark as Generated" placeholder invoices are written with type='monthly',
  // status='paid', amount=0, and metadata.invoice_source='manual_mark'.
  // The type='monthly' is load-bearing — the dues-run cron uses it for
  // dupe-detection — so we can't change the column. Instead we treat the
  // marker as a hybrid: show the line-items editor (so staff can convert
  // it into a real invoice) but skip the monthly-breakdown panel (it has
  // nothing real to break down).
  const isManualMarkPlaceholder =
    invoice.metadata?.invoice_source === "manual_mark" &&
    invoice.type === "monthly";

  const handleChange = (field: string, value: any) => {
    onChange({ ...invoice, [field]: value });
  };

  // Member-visible note (metadata.note). Surfaced here so staff can annotate
  // "what's due" on an already-published / auto-published invoice — drafts
  // already expose this via EditDraftInvoicePage, this closes the post-publish
  // gap. The save path spreads invoice.metadata, so updating it here is enough.
  const handleNoteChange = (value: string) => {
    onChange({ ...invoice, metadata: { ...invoice.metadata, note: value } });
  };

  const handleLineItemChange = (id: string, field: string, value: any) => {
    if (field !== "description") setAmountTouched(true);
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]:
                field === "description"
                  ? value
                  : isNaN(parseFloat(value))
                  ? 0
                  : parseFloat(value),
            }
          : item
      )
    );
  };

  const handleAddLineItem = () => {
    setAmountTouched(true);
    setLineItems((prev) => [
      ...prev,
      { id: uuidv4(), quantity: 1, description: "", unitPrice: 0, discount: 0 },
    ]);
  };

  const handleRemoveLineItem = (id: string) => {
    setAmountTouched(true);
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  const calculateLineTotal = (item: any) =>
    item.quantity * item.unitPrice - (item.discount || 0);

  const totalAmount =
    invoice.metadata?.purpose !== "installment_invoice" ||
    !invoice.metadata?.totalInstallments
      ? Array.isArray(lineItems)
        ? lineItems.reduce((sum, item) => sum + calculateLineTotal(item), 0)
        : 0
      : invoice.amount;

  const handleSave = async () => {
    if (!invoice) return;

    setSaving(true);
    try {
      let updatedData: any = { ...invoice };

      if (isPayment) {
        if (refundMode) {
          // 💸 Refund mode: create refund payload and trigger refund API
          const refundPayload = { invoiceId: invoice.id, refundAmount };
          // refundProcessPayment never throws: a refused refund comes back as
          // the server's own body ({ error } with no `success`). The result
          // used to be discarded, so the modal closed exactly as it does for
          // a refund that went through and staff were told nothing.
          const refundResult = await refundProcessPayment(refundPayload, token);
          if (!refundResult?.success) {
            toast.error(
              getPaymentErrorMessage(
                refundResult,
                "The refund did not go through. Nothing was refunded."
              ),
              { autoClose: 10000 }
            );
            return;
          }
          toast.success("Refund processed.");
          handelFetchMemberData?.();
          updatedData.refund = {
            amount: refundAmount,
            date: new Date().toISOString(),
          };
        } else {
          // 🧾 Regular payment edit: submit the updated payment data
          updatedData.amountChanged = amountTouched;
          onChange(updatedData);
          await onSubmit(updatedData);
        }
      } else {
        // 📦 Invoice editing (not payment)
        //
        // Pick the amount source based on which editor is actually rendered.
        // This MUST mirror the line-items render gate further down: monthly
        // dues and installment invoices hide the line-items table and are
        // edited via the single "Amount" input (which writes invoice.amount).
        // Recomputing from their hidden/empty line items clobbers the typed
        // value to 0 — that's the "type $200, saves as $0" bug Chris hit on a
        // `type:'monthly'` membership-dues invoice whose only line item had
        // unitPrice 0.
        const usesLineItemsEditor =
          (invoice.type !== "monthly" || isManualMarkPlaceholder) &&
          invoice.type !== "refund" &&
          (invoice.metadata?.purpose !== "installment_invoice" ||
            !invoice.metadata?.totalInstallments);

        let newAmount: number;
        if (!amountTouched) {
          // Nothing that sets the amount was touched: keep the stored amount.
          const storedAmount = Number(invoice.amount);
          newAmount = Number.isFinite(storedAmount) ? storedAmount : 0;
        } else if (usesLineItemsEditor) {
          // Line items are the source of truth. totalAmount can legitimately
          // be 0 (e.g. fully discounted to zero), so do NOT fall back to
          // invoice.amount on a falsy 0 — only on a non-finite value. The old
          // `totalAmount || invoice.amount` clobbered a $0 total back to the
          // original amount, so discounted-to-zero invoices kept charging.
          const computedTotal = Number(totalAmount);
          newAmount = Number.isFinite(computedTotal)
            ? computedTotal
            : invoice.amount;
        } else {
          // Amount-input-driven invoice (monthly dues / installment): the
          // typed invoice.amount is authoritative.
          const directAmount = Number(invoice.amount);
          newAmount = Number.isFinite(directAmount) ? directAmount : 0;
        }
        // Keep the metadata debit/credit display fields in sync with the new
        // total — the billing-history row and running balance read
        // metadata.debit/credit, so a stale value would keep showing the
        // pre-discount amount even after the charge changed.
        const isCreditInvoice = invoice.metadata?.type === "credit";
        updatedData = {
          ...invoice,
          amount: newAmount,
          // Tells the server this amount change is deliberate. Without it the
          // server refuses any edit that would change the amount.
          amountChanged: amountTouched,
          metadata: {
            ...invoice.metadata,
            // Persist line items ONLY when the line-items editor was actually
            // rendered — the same gate the amount uses above.
            //
            // Monthly dues and installment invoices hide that table and are
            // edited through the single Amount input, but `lineItems` state is
            // still seeded with a blank placeholder
            // ({quantity:1, unitPrice:0, description:""}) whenever the invoice
            // has none — which dues invoices never do; they carry
            // calculateData/slotBreakdown instead. Writing it unconditionally
            // stamped that placeholder onto the invoice, so a real $265 dues
            // bill ended up itemised as $0.00 and stayed that way.
            //
            // 75 GoY invoices totalling $9,869.18 are in that state
            // (CLUB-CENTRAL-BACKEND-1J); 62 are published, outstanding dues.
            // Staff see the drift banner in MemberInvoiceTooltip and $0 of
            // detail behind a real charge.
            //
            // And only when staff changed them: an untouched seeded line must
            // not be written onto an invoice that never had line items.
            ...(usesLineItemsEditor && amountTouched ? { lineItems } : {}),
            ...(isCreditInvoice
              ? { credit: newAmount }
              : { debit: newAmount }),
          },
        };

        onChange(updatedData);
        await onSubmit(updatedData);

      }
      setRefundAmount(0)
      onClose();
    } catch (error) {
      // Axios interceptor handles Sentry capture
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 ${TOKENS.overlay.backdrop} flex items-center justify-center z-50 transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className={`${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } rounded-lg shadow-lg w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto animate-fadeIn`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2
            className={`text-2xl font-semibold ${getPrimaryText(isDarkMode)}`}
          >
            {isPayment ? "Edit Payment" : "Edit Invoice"}
          </h2>
          <Button
            type="button"
            variant="neutral"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>

        {/* Basic Info */}
        <table
          data-onboarding="invoice-edit"
          className={`w-full text-sm text-left mt-4 ${
            isDarkMode ? "border-gray-700" : "border-gray-300"
          } border`}
        >
          <thead
            className={`${
              isDarkMode
                ? "bg-gray-800 text-gray-200"
                : "bg-gray-100 text-gray-900"
            }`}
          >
            <tr
              className={`text-left ${
                isDarkMode ? "bg-gray-700" : "bg-gray-100"
              }`}
            >
              <th className={`p-2 ${getSecondaryText(isDarkMode)}`}>
                Description
              </th>
              <th className={`p-2 ${getSecondaryText(isDarkMode)}`}>
                {isPayment ? "Payment Date" : "Due Date"}
              </th>
              <th className={`p-2 text-right ${getSecondaryText(isDarkMode)}`}>
                Amount
              </th>
            </tr>
          </thead>
          <tbody className={getPrimaryText(isDarkMode)}>
            <tr
              className={`border-t font-semibold ${
                isDarkMode
                  ? "bg-green-900 text-green-100"
                  : "bg-green-100 text-green-900"
              }`}
            >
              <td className="p-2">
                <input aria-label="Invoice description"
                  type="text"
                  value={invoice?.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className={`w-full px-2 py-1 border rounded ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </td>
              <td className="p-2">
                <input
                  aria-label="Due date"
                  name="due_date"
                  value={
                    invoice?.due_date
                      ? toDateOnlyYmd(invoice.due_date)
                      : ""
                  }
                  onChange={(e) => handleChange("due_date", e.target.value)}
                  type="date" autoComplete="off"
                  className={`w-full px-2 py-1 border rounded ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </td>

              {/* Editable amount if payment */}
              <td className="p-2 text-right font-semibold">
                {isPayment || invoice.type === "monthly" ? (
                  <input aria-label="Invoice amount"
                    type="number"
                    step="0.01"
                    value={invoice?.amount || 0}
                    onChange={(e) => {
                      setAmountTouched(true);
                      handleChange("amount", parseFloat(e.target.value) || 0);
                    }}
                    className={`w-full text-right px-2 py-1 border rounded ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-gray-100"
                        : "bg-white border-gray-300 text-gray-900"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                ) : (
                  formatCurrency(invoice?.amount || 0)
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Member-visible note — staff can write what the charge is "for"
            (e.g. "also due for remainder of party"). Shows to the member on
            their invoice. Hidden for payments. */}
        {!isPayment && (
          <div className="mt-4">
            <Textarea
              label="Note (shown to member)"
              value={invoice?.metadata?.note || ""}
              onChange={(e) => handleNoteChange(e.target.value)}
              rows={2}
              placeholder="Optional — e.g. 'also due for remainder of party'"
            />
          </div>
        )}

        {/* Breakdown */}
        {!isPayment && invoice.type === "monthly" && !isManualMarkPlaceholder && (
          <div
            className={`border-t pt-4 mt-6 ${
              isDarkMode ? "border-gray-700" : "border-gray-300"
            }`}
          >
            <InvoiceBreakdownTable
              invoiceData={invoice}
              dueDate={invoice?.dueDate}
            />
          </div>
        )}

        {/* Refund Section for Payments */}
        {isPayment && invoice?.type !== "refund" && (
          <div
            className={`mt-6 border-t pt-4 ${
              isDarkMode ? "border-gray-700" : "border-gray-300"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-2 ${getPrimaryText(
                isDarkMode
              )}`}
            >
              Refund Options
            </h3>
            <div className="flex items-center mb-3 gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={refundMode}
                  onChange={(e) => setRefundMode(e.target.checked)}
                />
                Enable Refund
              </label>
            </div>
            {refundMode && (
              <div className="flex items-center gap-3">
                <Input
                  label="Refund Amount:"
                  type="number"
                  min={0}
                  max={invoice.amount}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(Number(e.target.value))}
                  className="w-32"
                />
                <span className={`${getSecondaryText(isDarkMode)}`}>
                  (Max: {formatCurrency(invoice.amount)})
                </span>
              </div>
            )}
          </div>
        )}
        {/* Invoice Line Items */}
        {!isPayment &&
          (invoice.type !== "monthly" || isManualMarkPlaceholder) &&
          invoice?.type !== "refund" &&
          (invoice.metadata?.purpose !== "installment_invoice" ||
            !invoice.metadata?.totalInstallments) && (
            <>
              <div className="my-6">
                <Tooltip content="Add individual items to your invoice with quantities, descriptions, unit prices, and optional discounts.">
                  <h3
                    className={`text-lg font-semibold mb-2 cursor-help ${getPrimaryText(
                      isDarkMode
                    )}`}
                  >
                    Invoice Line Items
                  </h3>
                </Tooltip>

                <div
                  className={`grid grid-cols-[4rem_1fr_7rem_7rem_7rem_1.5rem] gap-2 mb-2 font-semibold ${getSecondaryText(
                    isDarkMode
                  )}`}
                >
                  <div className="text-center">Qty</div>
                  <div>Description</div>
                  <div className="text-center">Unit Price</div>
                  <div className="text-center">Discount</div>
                  <div className="text-right">Amount</div>
                  <div></div>
                </div>

                {lineItems.map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-[4rem_1fr_7rem_7rem_7rem_1.5rem] gap-2 mb-2 items-center"
                  >
                    <input aria-label={`Quantity for ${item.description || "this line item"}`}
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) =>
                        handleLineItemChange(
                          item.id,
                          "quantity",
                          e.target.value
                        )
                      }
                      className={`p-2 rounded text-center ${getInputStyles(
                        isDarkMode
                      )}`}
                    />
                    <ProductAutocomplete
                      value={item.description}
                      onChange={(val, product) => {
                        handleLineItemChange(item.id, "description", val);
                        if (product) {
                          handleLineItemChange(
                            item.id,
                            "unitPrice",
                            product.price || 0
                          );
                        }
                      }}
                    />
                    <input aria-label={`Unit price for ${item.description || "this line item"}`}
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={item.unitPrice}
                      onChange={(e) =>
                        handleLineItemChange(
                          item.id,
                          "unitPrice",
                          e.target.value
                        )
                      }
                      className={`p-2 rounded ${getInputStyles(isDarkMode)}`}
                    />
                    <input aria-label={`Discount for ${item.description || "this line item"}`}
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={item.discount}
                      onChange={(e) =>
                        handleLineItemChange(
                          item.id,
                          "discount",
                          e.target.value
                        )
                      }
                      className={`p-2 rounded ${getInputStyles(isDarkMode)}`}
                    />
                    <div className="text-right font-semibold">
                      {formatCurrency(calculateLineTotal(item))}
                    </div>
                    {lineItems.length > 1 ? (
                      <Button
                        variant="dangerGhost"
                        size="xs"
                        iconOnly
                        type="button"
                        onClick={() => handleRemoveLineItem(item.id)}
                        aria-label="Remove line item"
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    ) : (
                      <div></div>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={handleAddLineItem}
                  className="mt-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 px-4 py-1 rounded font-medium transition-colors"
                >
                  + Add Line Item
                </button>
              </div>

              <div
                className={`font-semibold flex items-center justify-end ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <div className={`p-2 text-right ${getPrimaryText(isDarkMode)}`}>
                  Total Amount:
                </div>
                <div
                  className={`p-3 text-right font-semibold ${getPrimaryText(
                    isDarkMode
                  )}`}
                >
                  {formatCurrency(totalAmount)}
                </div>
              </div>
            </>
          )}

        {/* Save Button */}
        <div className="flex justify-end mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`bg-[var(--color-save)] text-[var(--color-save-text)] hover:bg-[var(--color-save-hover)] font-semibold px-6 py-2 rounded-md transition-colors ${
              saving ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {saving
              ? isPayment
                ? "Updating Payment..."
                : "Updating Invoice..."
              : isPayment
              ? "Update Payment"
              : "Update Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditBillingInvoiceModal;
